import React from "react";

import type { SurveyQuestion } from "../../lib/api";
import {
  isChoiceType,
  isScaleType,
  isTextType,
  questionTypeLabel,
} from "./surveyTypes";

type Props = {
  questions: SurveyQuestion[];
};

/** Read-only render of the survey exactly as it was built. */
const SurveyQuestionsTab: React.FC<Props> = ({ questions }) => {
  if (questions.length === 0) {
    return (
      <p className="rounded-2xl border border-dashed border-[#2C363E] px-4 py-16 text-center text-[13px] text-[#5C6B76]">
        This survey has no questions yet.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {questions.map((q, i) => (
        <section
          key={q.key}
          className="rounded-2xl border border-[#2C363E] bg-[#161E25]/40 p-4 sm:p-5"
        >
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-[#5C6B76]">
                Question {i + 1} · {questionTypeLabel(q.type)}
                {q.required ? "" : " · Optional"}
              </p>
              <h3 className="mt-1 text-[15px] font-semibold leading-snug text-white">
                {q.prompt}
              </h3>
              {q.helpText && (
                <p className="mt-1 text-[12px] text-[#869AA9]">{q.helpText}</p>
              )}
            </div>
            <span className="shrink-0 rounded-md border border-[#3A4852] bg-[#1C2227] px-2 py-0.5 font-mono text-[10px] text-[#869AA9]">
              {q.key}
            </span>
          </div>

          {isChoiceType(q.type) && (
            <ul className="flex flex-wrap gap-2">
              {(q.options ?? []).map((o) => (
                <li
                  key={o.id}
                  className="rounded-lg border border-[#2C363E] bg-[#13181B] px-3 py-1.5 text-[12px] text-[#C6D0D7]"
                >
                  {o.label}
                </li>
              ))}
              {q.allowOther && (
                <li className="rounded-lg border border-dashed border-secondary_button/50 bg-secondary_button/10 px-3 py-1.5 text-[12px] font-medium text-secondary_button">
                  Other + free text
                </li>
              )}
            </ul>
          )}

          {isTextType(q.type) && (
            <div className="space-y-2.5">
              <p className="rounded-lg border border-[#2C363E] bg-[#13181B] px-3 py-2.5 text-[12px] italic text-[#5C6B76]">
                {q.placeholder || "Free text"}
                {q.maxLength ? ` · max ${q.maxLength} characters` : ""}
              </p>
              {q.suggestions && q.suggestions.length > 0 && (
                <div>
                  <p className="mb-1.5 text-[11px] text-[#869AA9]">
                    Suggested answers
                  </p>
                  <ul className="flex flex-wrap gap-1.5">
                    {q.suggestions.map((s) => (
                      <li
                        key={s.id}
                        className="rounded bg-[#daebf7]/10 px-2 py-1 text-[12px] font-semibold text-secondary_button"
                      >
                        {s.label}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {isScaleType(q.type) && (
            <p className="text-[12px] text-[#869AA9]">
              Scale {q.scale?.min ?? 0}–{q.scale?.max ?? 10}
              {q.scale?.minLabel ? ` · “${q.scale.minLabel}”` : ""}
              {q.scale?.maxLabel ? ` → “${q.scale.maxLabel}”` : ""}
            </p>
          )}
        </section>
      ))}
    </div>
  );
};

export default SurveyQuestionsTab;
